const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Path A age gate (#1006): the platform is adults-only. Register is the one
// place a DOB is captured, so the age threshold is enforced here (and again
// server-side in the part2 route). MIN_AGE also doubles as the fix for the
// "toddler DOB" data-entry typos (birth years in the current year etc.): an
// impossible-young age fails this check the same way a genuine minor does.
export const MIN_AGE = 18;

// Oldest plausible birth year. Anything older is a data-entry error, not a
// real member; anything in the future is impossible.
const MIN_BIRTH_YEAR = 1900;

export function isValidEmail(email: string): boolean {
  return EMAIL_FORMAT.test(email.trim());
}

/**
 * True when day/month/year form a real calendar date within a plausible birth
 * range (year 1900..current). This is date *validity* only — it says nothing
 * about age. Use {@link isAdult} for the 18+ gate.
 */
export function isValidDate(day: string, month: string, year: string): boolean {
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (
    !(d && m && y) ||
    y < MIN_BIRTH_YEAR ||
    y > new Date().getFullYear() ||
    day.length < 1 ||
    month.length < 1 ||
    year.length < 4
  ) {
    return false;
  }
  const date = new Date(y, m - 1, d);
  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  );
}

/**
 * Age in whole years for a valid day/month/year, or `null` if the date is not
 * a valid calendar date.
 */
export function getAge(
  day: string,
  month: string,
  year: string
): number | null {
  if (!isValidDate(day, month, year)) {
    return null;
  }
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  const today = new Date();
  const hasHadBirthdayThisYear =
    today.getMonth() > m - 1 ||
    (today.getMonth() === m - 1 && today.getDate() >= d);
  return today.getFullYear() - y - (hasHadBirthdayThisYear ? 0 : 1);
}

/**
 * True when the given day/month/year is a valid date AND the person is at
 * least {@link MIN_AGE} (18). Impossible-young typo dates (e.g. a current-year
 * birth year) correctly fail this too.
 */
export function isAdult(day: string, month: string, year: string): boolean {
  const age = getAge(day, month, year);
  return age !== null && age >= MIN_AGE;
}

const ISO_DATE_FORMAT = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Server-side equivalent of {@link isAdult} for the `YYYY-MM-DD` string the
 * client assembles and the part2 route receives. Rejects malformed strings,
 * impossible/future dates, and anyone under {@link MIN_AGE}. Register is
 * unauthenticated (email match only), so this is a best-effort block, not a
 * verified-identity guarantee — it stops the obvious cases and the toddler-DOB
 * typos, but a determined user can enter any date. Documented in #1006.
 */
export function isAdultIsoDob(iso: string): boolean {
  const match = ISO_DATE_FORMAT.exec(iso.trim());
  if (!match) {
    return false;
  }
  const year = match[1];
  const month = match[2];
  const day = match[3];
  if (!(year && month && day)) {
    return false;
  }
  // Reuse the day/month/year validators so plausibility + age stay in one place.
  return isAdult(String(Number(day)), String(Number(month)), year);
}
